import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package } from 'shared/icons';
import { SKU_FILTER_OPTIONS } from '../../constants/pricing';
import api from '../../services/api';
import { FormatDecimal1, FormatInt, FormatMoney } from '../../utils/format';
import { SecureMlImageUrl } from '../../utils/ml';
import { IntelStatCard } from '../marketIntelligence/intelligenceShared';
import ErrorCard from '../ui/ErrorCard';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { SkeletonTable } from '../ui/Skeleton';
import StandardTable from '../ui/StandardTable';
import { AlertBadges, DASH, FormatDay, IcmsTypePill, MatchedByLabel, Money } from './pricingShared';

/**
 * CUSTOS POR SKU — a lista do cadastro fiscal: um SKU por linha com a regra
 * aplicada (SKU → NCM → padrão), o último lote e o CMV líquido por unidade
 * (SP / fora / mix). Clique abre a página do SKU. Busca/filtro/paginação
 * são do servidor.
 */

const DEFAULT_PAGE_SIZE = 30;
const PAGE_SIZE_OPTIONS = [30, 50, 100];

const Thumb = ({ src }) => (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-900">
        {src ? (
            <img src={SecureMlImageUrl(src)} alt="" loading="lazy" className="h-full w-full object-contain" />
        ) : (
            <Package size={18} className="text-mono-300 dark:text-mono-700" />
        )}
    </div>
);

const COLUMNS = [
    {
        key: 'product',
        label: 'Produto',
        getValue: (row) => `${row.sku} ${row.product?.title || row.listing?.title || row.lot_description || ''}`,
        render: (row) => (
            <div className="flex min-w-0 items-center gap-3">
                <Thumb src={row.listing?.thumbnail} />
                <div className="min-w-0">
                    <p
                        className="truncate text-sm font-medium text-black dark:text-white"
                        title={row.product?.title || row.listing?.title || row.lot_description || ''}
                    >
                        {row.product?.title || row.listing?.title || row.lot_description || DASH}
                    </p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-1.5">
                        <span className="rounded-md bg-mono-100 px-1.5 py-px font-mono text-[11px] text-mono-600 dark:bg-mono-900 dark:text-mono-300">
                            {row.sku}
                        </span>
                        {row.product?.brand ? (
                            <span className="text-[11px] text-mono-500">{row.product.brand}</span>
                        ) : null}
                        {!row.product ? (
                            <span className="text-[11px] italic text-mono-400">sem cadastro Bling</span>
                        ) : null}
                    </p>
                </div>
            </div>
        ),
    },
    {
        key: 'rule',
        label: 'Tratamento',
        getValue: (row) => row.rule?.icms_type || '',
        render: (row) => (
            <div className="space-y-0.5">
                <IcmsTypePill type={row.rule?.icms_type} />
                <MatchedByLabel matchedBy={row.rule?.matched_by} />
            </div>
        ),
    },
    {
        key: 'last',
        label: 'Última NF-e',
        getValue: (row) => String(row.last_emitted_at || '').slice(0, 10),
        render: (row) =>
            row.latest ? (
                <div className="min-w-0">
                    <p className="font-mono text-xs">
                        {FormatDay(row.last_emitted_at)} · nº {row.latest.nfe_number || DASH}
                    </p>
                    <p className="truncate text-xs text-mono-500" title={row.latest.issuer_name || ''}>
                        {row.latest.issuer_name || DASH} · {FormatDecimal1(row.latest.qty)} un ·{' '}
                        {FormatInt(row.lots_count)} lote(s)
                    </p>
                </div>
            ) : (
                <span className="text-xs italic text-mono-400">sem nota</span>
            ),
    },
    {
        key: 'custo_bruto',
        label: 'Custo bruto/un',
        align: 'right',
        getValue: (row) => row.latest?.custo_bruto ?? -Infinity,
        render: (row) => <Money value={row.latest?.custo_bruto} />,
    },
    {
        key: 'creditos',
        label: 'Créditos/un',
        align: 'right',
        getValue: (row) => (row.latest ? (row.latest.credito_icms || 0) + (row.latest.credito_pc || 0) : -Infinity),
        render: (row) =>
            row.latest ? (
                <span
                    className="tabular-nums text-success-700 dark:text-success-400"
                    title={`ICMS ${FormatMoney(row.latest.credito_icms)} · PIS/COFINS ${FormatMoney(row.latest.credito_pc)}`}
                >
                    −{FormatMoney((row.latest.credito_icms || 0) + (row.latest.credito_pc || 0))}
                </span>
            ) : (
                <span className="text-mono-400">{DASH}</span>
            ),
    },
    {
        key: 'cmv_sp',
        label: 'CMV SP/un',
        align: 'right',
        getValue: (row) => row.latest?.cmv_sp ?? -Infinity,
        render: (row) => <Money value={row.latest?.cmv_sp} />,
    },
    {
        key: 'cmv_fora',
        label: 'CMV fora/un',
        align: 'right',
        getValue: (row) => row.latest?.cmv_fora ?? -Infinity,
        render: (row) => <Money value={row.latest?.cmv_fora} />,
    },
    {
        key: 'cmv_blend',
        label: 'CMV mix/un',
        align: 'right',
        getValue: (row) => row.latest?.cmv_blend ?? -Infinity,
        render: (row) => (
            <div className="text-right">
                <Money value={row.latest?.cmv_blend} className="font-semibold" />
                {row.weighted?.cmv_blend !== null && row.weighted?.cmv_blend !== undefined && row.lots_count > 1 ? (
                    <p className="text-[11px] text-mono-500">média {FormatMoney(row.weighted.cmv_blend)}</p>
                ) : null}
            </div>
        ),
    },
    {
        key: 'bling',
        label: 'Custo Bling',
        align: 'right',
        getValue: (row) => row.product?.cost_price ?? -Infinity,
        render: (row) => <span className="tabular-nums text-mono-500">{FormatMoney(row.product?.cost_price)}</span>,
    },
    {
        key: 'alerts',
        label: 'Alertas',
        sortable: false,
        getValue: (row) => row.alerts.map((alert) => alert.code).join(' '),
        render: (row) => <AlertBadges alerts={row.alerts} hide={['SEM_CADASTRO']} compact />,
    },
];

const SkuCostList = ({ onGoToTab }) => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [loading, setLoading] = useState(true);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [error, setError] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const payload = await api.getPricingSkus({
                    search,
                    filter,
                    limit: pageSize,
                    offset: (page - 1) * pageSize,
                });
                if (!cancelled) {
                    setRows(Array.isArray(payload?.items) ? payload.items : []);
                    setTotal(Number(payload?.total || 0));
                    setSummary(payload?.summary || null);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || 'Não foi possível carregar os SKUs.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    setHasLoadedOnce(true);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [search, filter, page, pageSize, refreshTick]);

    const applySearch = () => {
        const term = searchInput.trim();
        setSearchInput(term);
        if (term !== search) {
            setSearch(term);
            setPage(1);
        }
    };

    return (
        <div className="space-y-4">
            {summary ? (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                    <IntelStatCard
                        label="SKUs com NF-e de compra"
                        value={FormatInt(summary.with_lots)}
                        sub={`de ${FormatInt(summary.skus_total)} no recorte`}
                    />
                    <IntelStatCard
                        label="Sem regra de ICMS"
                        value={FormatInt(summary.without_rule)}
                        sub="usando o padrão da empresa"
                        tone={summary.without_rule > 0 ? 'warning' : 'mono'}
                        onClick={() => {
                            setFilter('sem_regra');
                            setPage(1);
                        }}
                    />
                    <IntelStatCard
                        label="ST estimado"
                        value={FormatInt(summary.st_estimado)}
                        sub="nota sem vICMSSTRet"
                        tone={summary.st_estimado > 0 ? 'danger' : 'mono'}
                        onClick={() => {
                            setFilter('st_estimado');
                            setPage(1);
                        }}
                    />
                    <IntelStatCard
                        label="Vínculo a confirmar"
                        value={FormatInt(summary.suggested_links || 0)}
                        sub="de-para sugerido pelo importador"
                        tone={summary.suggested_links > 0 ? 'warning' : 'mono'}
                        onClick={onGoToTab ? () => onGoToTab('suppliers') : null}
                    />
                    <IntelStatCard
                        label="Itens de nota sem produto"
                        value={FormatInt(summary.unmatched_lots)}
                        sub="vincular no de-para"
                        tone={summary.unmatched_lots > 0 ? 'danger' : 'mono'}
                        onClick={onGoToTab ? () => onGoToTab('suppliers') : null}
                    />
                </div>
            ) : null}

            <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[220px] max-w-md flex-1">
                    <Input
                        label="Buscar"
                        type="search"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        onBlur={applySearch}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                applySearch();
                            }
                        }}
                        placeholder="SKU, título ou descrição da nota..."
                    />
                </div>
                <div className="w-64">
                    <Select
                        label="Situação"
                        value={filter}
                        onChange={(event) => {
                            setFilter(event.target.value);
                            setPage(1);
                        }}
                    >
                        {SKU_FILTER_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </div>
            </div>

            {error ? (
                <ErrorCard
                    title="Erro ao carregar"
                    message={error}
                    action="Tentar novamente"
                    actionFn={() => setRefreshTick((tick) => tick + 1)}
                />
            ) : !hasLoadedOnce ? (
                <SkeletonTable columns={8} rows={8} />
            ) : (
                <StandardTable
                    rows={rows}
                    columns={COLUMNS}
                    rowKey={(row) => row.sku}
                    onRowClick={(row) => navigate(`/precificacao/sku/${encodeURIComponent(row.sku)}`)}
                    rowClassName={() => 'cursor-pointer'}
                    countLabel="SKUs"
                    emptyMessage={
                        search
                            ? `Nenhum SKU encontrado para “${search}”.`
                            : 'Nenhum SKU ainda — importe uma NF-e de compra na aba Notas de entrada.'
                    }
                    exportFileName="custos-por-sku"
                    searchEnabled={false}
                    paginationMode="server"
                    totalItems={total}
                    currentPage={page}
                    onPageChange={setPage}
                    itemsPerPage={pageSize}
                    onItemsPerPageChange={setPageSize}
                    defaultPageSize={DEFAULT_PAGE_SIZE}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    toolbarLeft={loading ? <span className="text-xs text-mono-500">Atualizando…</span> : null}
                />
            )}
        </div>
    );
};

export default SkuCostList;
